import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Phone, Mail, Globe, BadgeCheck, Send, Heart, AlertTriangle, Info, Maximize2, X, MessageSquare } from '../icons';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Zoom, Thumbs, EffectCreative, Keyboard, Parallax } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';
import 'swiper/css/effect-creative';
import 'swiper/css/parallax';
import StarRating from '../components/StarRating';
import { getVendor, inquireVendor, checkBookmark, bookmarkVendor, removeBookmark, getMyVendorInquiry, getAppSettings, sendInquiryMessage, trackVendorClick } from '../services/api';
import { joinInquiry, leaveInquiry, onNewMessage, onTyping, onStopTyping, emitTyping, emitStopTyping } from '../services/socket';
import { useAuth } from '../context/AuthContext';
import { vendorImage, vendorPhotoSrc } from '../utils/categoryImages';
import { useLanguage } from '../context/LanguageContext';
import mediaUrl from '../utils/mediaUrl';

function VendorDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [activeTab, setActiveTab] = useState('about');
  const [showInquiry, setShowInquiry] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({ message: '', wedding_date: '', guest_count: '' });
  const [inquirySending, setInquirySending] = useState(false);
  const [inquirySuccess, setInquirySuccess] = useState(false);
  const [inquiryError, setInquiryError] = useState('');
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);
  const [existingInquiry, setExistingInquiry] = useState(null);
  const [v2vBlocked, setV2vBlocked] = useState(false);
  const [coupleReply, setCoupleReply] = useState('');
  const [coupleReplyFile, setCoupleReplyFile] = useState(null);
  const [coupleSending, setCoupleSending] = useState(false);
  const [vendorTyping, setVendorTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  const convoEndRef = useRef(null);

  const openLightbox = useCallback((idx) => {
    setLightboxIndex(idx);
    setLightboxOpen(true);
  }, []);

  // Close lightbox on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setLightboxOpen(false); };
    if (lightboxOpen) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [lightboxOpen]);

  useEffect(() => {
    setLoadFailed(false);
    getVendor(slug)
      .then((res) => {
        setVendor(res.data);
        if (res.data?.id) trackVendorClick(res.data.id, 'view');
      })
      // Only a 404 means the vendor is gone; anything else is worth a retry.
      .catch((err) => { if (err.response?.status !== 404) setLoadFailed(true); })
      .finally(() => setLoading(false));
  }, [slug, reloadKey]);

  // Logged-out visitors are sent to log in; the redirect brings them back here.
  const loginHere = () => navigate(`/login?redirect=${encodeURIComponent(`/vendor/${slug}`)}`);

  // Check bookmark status & load existing inquiry
  useEffect(() => {
    if (user && vendor) {
      checkBookmark(vendor.id).then(res => setIsBookmarked(res.data.bookmarked)).catch(() => {});
      if (user.role === 'couple') {
        getMyVendorInquiry(vendor.id).then(res => { if (res.data) setExistingInquiry(res.data); }).catch(() => {});
      }
      if (user.role === 'vendor') {
        getAppSettings().then(res => {
          if (res.data?.vendor_to_vendor_enabled !== 'true') setV2vBlocked(true);
        }).catch(() => {});
      }
    }
  }, [user, vendor]);

  // Socket.IO: join inquiry room and listen for real-time messages
  useEffect(() => {
    if (!existingInquiry?.id) return;
    const inquiryId = existingInquiry.id;
    joinInquiry(inquiryId);

    const unsubMsg = onNewMessage(({ inquiryId: msgInqId, message }) => {
      if (msgInqId === inquiryId) {
        setExistingInquiry(prev => {
          if (!prev) return prev;
          const msgs = prev.messages || [];
          if (msgs.find(m => m.id === message.id)) return prev;
          return { ...prev, messages: [...msgs, message] };
        });
        // Auto-scroll
        setTimeout(() => convoEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
      }
    });

    const unsubTyping = onTyping(({ inquiryId: tid, senderType }) => {
      if (tid === inquiryId && senderType === 'vendor') setVendorTyping(true);
    });
    const unsubStopTyping = onStopTyping(({ inquiryId: tid, senderType }) => {
      if (tid === inquiryId && senderType === 'vendor') setVendorTyping(false);
    });

    return () => {
      leaveInquiry(inquiryId);
      unsubMsg();
      unsubTyping();
      unsubStopTyping();
    };
  }, [existingInquiry?.id]);

  const toggleBookmark = async () => {
    if (!user) { loginHere(); return; }
    setBookmarkLoading(true);
    try {
      if (isBookmarked) {
        await removeBookmark(vendor.id);
        setIsBookmarked(false);
      } else {
        await bookmarkVendor(vendor.id);
        setIsBookmarked(true);
      }
    } catch (err) {
      console.error('Bookmark toggle failed', err);
      alert(err.response?.data?.error || t('vendorDetail.bookmarkFailed'));
    }
    finally { setBookmarkLoading(false); }
  };

  if (loading) return <div className="loading-page"><div className="spinner"></div></div>;
  if (!vendor) {
    return (
      <div className="error-page">
        <h2>{loadFailed ? t('vendorDetail.loadError') : t('vendorDetail.notFound')}</h2>
        {!loadFailed && <p>{t('vendorDetail.notFoundDesc')}</p>}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginTop: 16 }}>
          {loadFailed && (
            <button className="btn btn-primary" onClick={() => { setLoading(true); setReloadKey((k) => k + 1); }}>{t('common.retry')}</button>
          )}
          <button className="btn btn-outline" onClick={() => navigate('/vendors')}>{t('footer.browseVendors')}</button>
        </div>
      </div>
    );
  }

  // Computed review data (available after vendor loads). A malformed cached
  // value must not take the whole page down.
  let googleReviews = [];
  try {
    googleReviews = typeof vendor.google_reviews === 'string'
      ? JSON.parse(vendor.google_reviews || '[]')
      : (vendor.google_reviews || []);
  } catch { googleReviews = []; }
  if (!Array.isArray(googleReviews)) googleReviews = [];
  const googleApiTotal = vendor.review_count ? parseInt(vendor.review_count) : googleReviews.length;
  const totalReviews = (vendor.reviews?.length || 0) + googleReviews.length;

  const priceLabels = {
    budget: t('vendorCard.budgetFriendly'), moderate: t('vendorCard.moderate'), premium: t('vendorCard.premium'), luxury: t('vendorCard.luxury'),
  };
  // Vendors often type "www.example.com"; without a scheme the link resolved
  // relative to nepwed.com and 404'd. Anything that isn't http(s) is not linked.
  const websiteHref = (() => {
    const w = (vendor.website || '').trim();
    if (!w) return null;
    const withScheme = /^https?:\/\//i.test(w) ? w : (/^[a-z][a-z0-9+.-]*:/i.test(w) ? null : `https://${w}`);
    return withScheme;
  })();
  const locationText = [vendor.city, vendor.district && vendor.district !== vendor.city ? vendor.district : null].filter(Boolean).join(', ');

  return (
    <div className="vendor-detail-page">
      {/* Hero */}
      <div className="vendor-hero">
        <img
          src={vendorImage(vendor, 1600, 600)}
          alt={vendor.business_name}
          className="vendor-hero-img"
        />
        <div className="vendor-hero-overlay">
          <div className="container">
            <div className="vendor-hero-content">
              <span className="vendor-category-badge">{vendor.category_name}</span>
              <h1>{vendor.business_name}</h1>
              <div className="vendor-hero-meta">
                <StarRating rating={parseFloat(vendor.rating)} size={20} />
                <span className="rating-text">{vendor.rating} ({totalReviews} {t('vendorDetail.reviews')})</span>
                <span className="meta-divider">|</span>
                {locationText && (
                  <>
                    <MapPin size={16} />
                    <span>{locationText}</span>
                  </>
                )}
                {priceLabels[vendor.price_range] && (
                  <>
                    <span className="meta-divider">|</span>
                    <span className="price-badge">{priceLabels[vendor.price_range]}</span>
                  </>
                )}
              </div>
              {vendor.is_verified && (
                <span className="verified-tag"><BadgeCheck size={16} /> {t('vendorDetail.verifiedVendor')}</span>
              )}
            </div>
          </div>
        </div>
      </div>



      <div className="container vendor-detail-container">
        <div className="vendor-main">
          {/* Tabs */}
          <div className="vendor-tabs">
            <button className={activeTab === 'about' ? 'active' : ''} onClick={() => setActiveTab('about')}>{t('vendorDetail.aboutTab')}</button>
            <button className={activeTab === 'photos' ? 'active' : ''} onClick={() => setActiveTab('photos')}>{t('vendorDetail.photosTab')} ({vendor.photos?.length || 0})</button>
            <button className={activeTab === 'reviews' ? 'active' : ''} onClick={() => setActiveTab('reviews')}>{t('vendorDetail.reviewsTab')} ({totalReviews})</button>
          </div>

          {/* About Tab */}
          {activeTab === 'about' && (
            <div className="tab-content">
              <h2>{t('vendorDetail.about')} {vendor.business_name}</h2>
              <p className="vendor-description">{vendor.description}</p>

              <div className="vendor-info-grid">
                {vendor.phone && (
                  <div className="info-item">
                    <Phone size={18} />
                    <div>
                      <label>{t('vendorDetail.phone')}</label>
                      <span>{vendor.phone}</span>
                    </div>
                  </div>
                )}
                {vendor.email && (
                  <div className="info-item">
                    <Mail size={18} />
                    <div>
                      <label>{t('vendorDetail.email')}</label>
                      <span>{vendor.email}</span>
                    </div>
                  </div>
                )}
                {vendor.website && (
                  <div className="info-item">
                    <Globe size={18} />
                    <div>
                      <label>{t('vendorDetail.website')}</label>
                      {websiteHref
                        ? <a href={websiteHref} target="_blank" rel="noreferrer">{vendor.website}</a>
                        : <span>{vendor.website}</span>}
                    </div>
                  </div>
                )}
                {vendor.address && (
                  <div className="info-item">
                    <MapPin size={18} />
                    <div>
                      <label>{t('vendorDetail.address')}</label>
                      <span>{vendor.address}</span>
                    </div>
                  </div>
                )}
              </div>

              {vendor.min_price_npr && (
                <div className="pricing-section">
                  <h3>{t('vendorDetail.pricing')}</h3>
                  <div className="price-range-display">
                    <span className="price-label">{t('vendorDetail.startingFrom')}</span>
                    <span className="price-value">NPR {Number(vendor.min_price_npr).toLocaleString()}</span>
                    <span className="price-separator">{t('vendorDetail.to')}</span>
                    <span className="price-value">NPR {Number(vendor.max_price_npr).toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Photos Tab — Enhanced Swiper Gallery */}
          {activeTab === 'photos' && (() => {
            const imagePhotos = (vendor.photos || []).filter(p => p.media_type !== 'youtube');
            const videoPhotos = (vendor.photos || []).filter(p => p.media_type === 'youtube');
            const getYtId = (url) => { const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]+)/); return m ? m[1] : null; };
            return (
            <div className="tab-content">
              <h2>{videoPhotos.length > 0 ? t('vendorDetail.photosVideos') : t('vendorDetail.photosTab')}</h2>
              {imagePhotos.length > 0 ? (
                <div className="vendor-gallery">
                  {/* Main Swiper — Creative Effect + Parallax */}
                  <Swiper
                    modules={[Navigation, Pagination, Zoom, Thumbs, EffectCreative, Keyboard, Parallax]}
                    navigation
                    pagination={{ clickable: true, dynamicBullets: true }}
                    zoom={{ maxRatio: 3 }}
                    keyboard={{ enabled: true }}
                    parallax
                    effect="creative"
                    creativeEffect={{
                      prev: { shadow: true, translate: ['-20%', 0, -1] },
                      next: { translate: ['100%', 0, 0] },
                    }}
                    thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                    spaceBetween={0}
                    slidesPerView={1}
                    className="vendor-gallery-main"
                  >
                    {imagePhotos.map((photo, idx) => (
                      <SwiperSlide key={photo.id}>
                        <div className="swiper-zoom-container">
                          <img
                            src={vendorPhotoSrc(photo.photo_url, vendor, 800, 500)}
                            alt={photo.caption || vendor.business_name}
                            loading="lazy"
                            data-swiper-parallax="-100"
                          />
                        </div>
                        <button className="gallery-fullscreen-btn" onClick={() => openLightbox(idx)} title="Fullscreen">
                          <Maximize2 size={18} />
                        </button>
                        {photo.caption && (
                          <div className="gallery-caption" data-swiper-parallax="-50" data-swiper-parallax-opacity="0">
                            {photo.caption}
                          </div>
                        )}
                        <div className="gallery-counter">{idx + 1} / {imagePhotos.length}</div>
                      </SwiperSlide>
                    ))}
                  </Swiper>

                  {/* Thumbnails */}
                  <Swiper
                    modules={[Thumbs, Navigation]}
                    onSwiper={setThumbsSwiper}
                    spaceBetween={8}
                    slidesPerView={Math.min(imagePhotos.length, 8)}
                    watchSlidesProgress
                    navigation
                    className="vendor-gallery-thumbs"
                  >
                    {imagePhotos.map((photo) => (
                      <SwiperSlide key={`thumb-${photo.id}`}>
                        <img
                          src={vendorPhotoSrc(photo.photo_url, vendor, 200, 150)}
                          alt={photo.caption || vendor.business_name}
                          loading="lazy"
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              ) : (
                !videoPhotos.length && (
                  <div className="empty-state">
                    <p>{t('vendorDetail.noPhotos')}</p>
                  </div>
                )
              )}

              {/* YouTube Videos */}
              {videoPhotos.length > 0 && (
                <div style={{ marginTop: imagePhotos.length > 0 ? '2rem' : 0 }}>
                  <h3 style={{ fontSize: '1.1rem', color: '#374151', marginBottom: '16px' }}>{t('vendorDetail.videos')} ({videoPhotos.length})</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
                    {videoPhotos.map((vid) => {
                      const ytId = getYtId(vid.photo_url);
                      return (
                        <div key={vid.id} style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #e5e7eb', background: '#fff' }}>
                          {ytId ? (
                            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                              <iframe
                                src={`https://www.youtube.com/embed/${ytId}`}
                                title={vid.caption || 'Video'}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                              />
                            </div>
                          ) : (
                            <a href={vid.photo_url} target="_blank" rel="noreferrer" style={{ display: 'block', padding: '40px', textAlign: 'center', background: '#f3f4f6', color: '#4b5563', textDecoration: 'none' }}>
                              {t('vendorDetail.viewOnYoutube')}
                            </a>
                          )}
                          {vid.caption && <div style={{ padding: '10px 14px', fontSize: '13px', color: '#6b7280' }}>{vid.caption}</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            );
          })()}

          {/* Fullscreen Lightbox */}
          {lightboxOpen && vendor.photos?.filter(p => p.media_type !== 'youtube').length > 0 && (() => {
            const imgPhotos = vendor.photos.filter(p => p.media_type !== 'youtube');
            return (
            <div className="gallery-lightbox" onClick={() => setLightboxOpen(false)}>
              <button className="lightbox-close" onClick={() => setLightboxOpen(false)}><X size={24} /></button>
              <div onClick={(e) => e.stopPropagation()} style={{ width: '95vw', height: '90vh' }}>
                <Swiper
                  modules={[Navigation, Pagination, Zoom, Keyboard]}
                  navigation
                  pagination={{ clickable: true }}
                  zoom={{ maxRatio: 5 }}
                  keyboard={{ enabled: true }}
                  initialSlide={lightboxIndex}
                  spaceBetween={0}
                  slidesPerView={1}
                  className="lightbox-swiper"
                >
                  {imgPhotos.map((photo) => (
                    <SwiperSlide key={`lb-${photo.id}`}>
                      <div className="swiper-zoom-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '90vh' }}>
                        <img
                          src={vendorPhotoSrc(photo.photo_url, vendor, 1200, 800)}
                          alt={photo.caption || vendor.business_name}
                          style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain' }}
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </div>
            );
          })()}

          {/* Reviews Tab */}
          {activeTab === 'reviews' && (
            <div className="tab-content">
              <h2>{t('vendorDetail.reviewsTab')}</h2>
              <div className="reviews-summary">
                <div className="rating-big">
                  <span className="rating-number">{vendor.rating}</span>
                  <StarRating rating={parseFloat(vendor.rating)} size={24} />
                  <span>{totalReviews} {t('vendorDetail.reviews')}</span>
                </div>
              </div>

              {/* NepWed User Reviews */}
              {vendor.reviews?.length > 0 && (
                <>
                  <h3 style={{ margin: '1.5rem 0 1rem', fontSize: '1.1rem', color: '#374151' }}>{t('vendorDetail.nepwedReviews')}</h3>
                  <div className="reviews-list">
                    {vendor.reviews.map((review) => (
                      <div key={review.id} className="review-card">
                        <div className="review-header">
                          <div className="review-author">
                            <div className="review-avatar">{review.first_name[0]}{review.last_name[0]}</div>
                            <div>
                              <strong>{review.first_name} {review.last_name}</strong>
                              <span className="review-date">{new Date(review.created_at).toLocaleDateString('en-NP')}</span>
                            </div>
                          </div>
                          <StarRating rating={review.rating} />
                        </div>
                        <h4>{review.title}</h4>
                        <p>{review.body}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Google Reviews */}
              {(() => {
                const googleMapsUrl = vendor.google_place_id
                  ? `https://search.google.com/local/reviews?placeid=${vendor.google_place_id}`
                  : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(vendor.business_name + ' ' + vendor.city)}`;
                return googleReviews.length > 0 ? (
                  <>
                    <h3 style={{ margin: '1.5rem 0 0.5rem', fontSize: '1.1rem', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <img src="https://www.google.com/favicon.ico" alt="Google" style={{ width: 16, height: 16 }} />
                      {t('vendorDetail.googleReviews')}
                      {googleApiTotal > 0 && (
                        <span style={{ fontSize: '13px', fontWeight: 400, color: '#6b7280' }}>({googleApiTotal})</span>
                      )}
                    </h3>
                    {user && user.role === 'admin' && googleApiTotal > googleReviews.length && (
                      <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <Info size={14} />
                        Admin: Google API returned {googleReviews.length} of {googleApiTotal} total reviews.
                      </p>
                    )}
                    <div className="reviews-list">
                      {googleReviews.map((review, idx) => (
                        <div key={`google-${idx}`} className="review-card">
                          <div className="review-header">
                            <div className="review-author">
                              {review.profile_photo ? (
                                <img src={review.profile_photo} alt={review.author} className="review-avatar-img" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />
                              ) : (
                                <div className="review-avatar">{(review.author || 'G')[0]}</div>
                              )}
                              <div>
                                <strong>{review.author}</strong>
                                <span className="review-date">{review.relative_time}</span>
                              </div>
                            </div>
                            <StarRating rating={review.rating} />
                          </div>
                          <p>{review.text}</p>
                        </div>
                      ))}
                    </div>
                    <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                      <a
                        href={googleMapsUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '8px',
                          background: '#fff', border: '1px solid #dadce0', borderRadius: '24px',
                          padding: '10px 24px', color: '#1a73e8', fontWeight: 600, fontSize: '14px',
                          textDecoration: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                          transition: 'box-shadow 0.2s, background 0.2s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)'; e.currentTarget.style.background = '#f8f9fa'; }}
                        onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)'; e.currentTarget.style.background = '#fff'; }}
                      >
                        <img src="https://www.google.com/favicon.ico" alt="Google" style={{ width: 18, height: 18 }} />
                        {t('vendorDetail.viewAllGoogle')}
                      </a>
                    </div>
                  </>
                ) : null;
              })()}

              {/* No reviews at all */}
              {(!vendor.reviews || vendor.reviews.length === 0) && googleReviews.length === 0 && (
                <div className="empty-state" style={{ marginTop: '1rem' }}>
                  <p>{t('vendorDetail.noReviews')}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="vendor-sidebar">
          {!vendor.is_verified && (
            <div className="sidebar-card" style={{ background: '#fff7ed', border: '1px solid #fb923c' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                <Info size={18} color="#c2410c" />
                <strong style={{ color: '#c2410c', fontSize: '14px' }}>{t('vendorDetail.unverifiedListing')}</strong>
              </div>
              <p style={{ color: '#7c2d12', fontSize: '12px', margin: 0, lineHeight: 1.5 }}>
                {t('vendorDetail.unverifiedWarning')}
              </p>
            </div>
          )}
          <div className="sidebar-card inquiry-card">
            <div className="inquiry-card-banner">
              <Heart size={24} strokeWidth={1.5} />
              <div>
                <h3>{t('vendorDetail.interested')}</h3>
                <p>{t('vendorDetail.requestPricing')} {vendor.business_name}</p>
              </div>
            </div>
            <div className="inquiry-card-body">
              {/* Bookmark Button — only for verified vendors */}
              {vendor.is_verified ? (
                <button
                  className={`btn btn-block ${isBookmarked ? 'btn-primary' : 'btn-outline'}`}
                  onClick={toggleBookmark}
                  disabled={bookmarkLoading}
                  style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                >
                  <Heart size={16} fill={isBookmarked ? '#fff' : 'none'} /> {isBookmarked ? t('vendorDetail.bookmarked') : t('vendorDetail.bookmarkVendor')}
                </button>
              ) : (
                <p style={{ fontSize: '12px', color: '#9ca3af', textAlign: 'center', marginBottom: '10px' }}>{t('vendorDetail.bookmarkVerifiedOnly')}</p>
              )}
              {v2vBlocked ? (
                <p style={{ color: '#9ca3af', fontSize: '12px', textAlign: 'center', margin: '8px 0 0' }}>
                  {t('vendorDetail.vendorToVendorDisabled')}
                </p>
              ) : (
                <>
                  {/* A suspended or unapproved vendor can't log in to answer, so
                      the server refuses new inquiries for them too. */}
                  {vendor.is_verified && vendor.approval_status === 'approved' ? (
                    !existingInquiry && (
                      <button className="btn btn-primary btn-block" onClick={() => {
                        if (!user) { loginHere(); return; }
                        setShowInquiry(true);
                      }}>
                        <Send size={16} /> {t('vendorDetail.sendInquiry')}
                      </button>
                    )
                  ) : (
                    <>
                      <button className="btn btn-primary btn-block" disabled style={{ opacity: 0.5, cursor: 'not-allowed', background: '#9ca3af' }}>
                        <Send size={16} /> {t('vendorDetail.sendInquiry')}
                      </button>
                      <p style={{ color: '#9ca3af', fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                        <AlertTriangle size={12} style={{ verticalAlign: 'middle' }} /> {vendor.is_verified ? t('vendorDetail.notAcceptingInquiries') : t('vendorDetail.onlyVerified')}
                      </p>
                    </>
                  )}
                  {vendor.phone && (
                    vendor.is_verified ? (
                      <a href={`tel:${vendor.phone}`} className="btn btn-outline btn-block">
                        <Phone size={16} /> {t('vendorDetail.callNow')}
                      </a>
                    ) : (
                      <button className="btn btn-outline btn-block" disabled style={{ opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' }}>
                        <Phone size={16} /> {t('vendorDetail.callNow')}
                      </button>
                    )
                  )}
                </>
              )}
            </div>
          </div>

          {/* Existing Conversation */}
          {existingInquiry && (
            <div className="sidebar-card" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <h3 style={{ fontSize: '15px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <MessageSquare size={16} /> {t('vendorDetail.yourConversation')}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '350px', overflowY: 'auto', marginBottom: '12px' }}>
                {(existingInquiry.messages && existingInquiry.messages.length > 0) ? (
                  existingInquiry.messages.map((msg) => (
                    <div key={msg.id} style={{
                      background: '#fff', borderRadius: '10px', padding: '10px 12px',
                      border: `1px solid ${msg.sender_type === 'couple' ? '#e5e7eb' : '#BFDBFE'}`,
                      marginLeft: msg.sender_type === 'couple' ? 0 : '12px',
                      marginRight: msg.sender_type === 'couple' ? '12px' : 0,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 600, color: msg.sender_type === 'couple' ? '#4f46e5' : '#059669' }}>
                          {msg.sender_type === 'couple' ? t('vendorDetail.you') : vendor.business_name}
                        </span>
                        <span style={{ fontSize: '10px', color: '#9ca3af' }}>{new Date(msg.created_at).toLocaleDateString()}</span>
                      </div>
                      <p style={{ fontSize: '13px', color: '#374151', margin: 0, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{msg.message}</p>
                      {msg.attachment_url && (
                        <a href={mediaUrl(msg.attachment_url)} target="_blank" rel="noopener noreferrer"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '4px', fontSize: '12px', color: '#1D4ED8', textDecoration: 'none' }}>
                          📎 {msg.attachment_name || 'Attachment'}
                        </a>
                      )}
                    </div>
                  ))
                ) : (
                  <>
                    {/* Fallback: show old-style single message + reply */}
                    <div style={{ background: '#fff', borderRadius: '10px', padding: '10px 12px', border: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#4f46e5' }}>{t('vendorDetail.you')}</span>
                        <span style={{ fontSize: '10px', color: '#9ca3af' }}>{new Date(existingInquiry.created_at).toLocaleDateString()}</span>
                      </div>
                      <p style={{ fontSize: '13px', color: '#374151', margin: 0, lineHeight: 1.5 }}>{existingInquiry.message}</p>
                    </div>
                    {existingInquiry.vendor_reply ? (
                      <div style={{ background: '#fff', borderRadius: '10px', padding: '10px 12px', border: '1px solid #BFDBFE', marginLeft: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 600, color: '#059669' }}>{vendor.business_name}</span>
                          <span style={{ fontSize: '10px', color: '#9ca3af' }}>{new Date(existingInquiry.vendor_reply_at).toLocaleDateString()}</span>
                        </div>
                        <p style={{ fontSize: '13px', color: '#374151', margin: 0, lineHeight: 1.5 }}>{existingInquiry.vendor_reply}</p>
                      </div>
                    ) : (
                      <p style={{ fontSize: '12px', color: '#6b7280', textAlign: 'center', margin: 0 }}>{t('vendorDetail.awaitingReply')}</p>
                    )}
                  </>
                )}
                {/* Inside the scrolling box, so new messages scroll it rather than the page */}
                <div ref={convoEndRef} />
              </div>
              {/* Typing indicator */}
              {vendorTyping && (
                <div style={{ fontSize: '12px', color: '#059669', fontStyle: 'italic', marginBottom: '6px', padding: '0 4px' }}>
                  {vendor.business_name} {t('vendorDetail.isTyping')}
                </div>
              )}
              {/* Couple reply box */}
              {user && user.role === 'couple' && (
                <div style={{ borderTop: '1px solid #d1fae5', paddingTop: '10px' }}>
                  <textarea
                    rows={2}
                    value={coupleReply}
                    onChange={(e) => {
                      setCoupleReply(e.target.value);
                      if (existingInquiry?.id) {
                        emitTyping(existingInquiry.id);
                        clearTimeout(typingTimeoutRef.current);
                        typingTimeoutRef.current = setTimeout(() => emitStopTyping(existingInquiry.id), 2000);
                      }
                    }}
                    placeholder={t('vendorDetail.sendMessagePlaceholder')}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: '6px' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#6b7280', cursor: 'pointer', padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#f9fafb' }}>
                      📎 {coupleReplyFile ? coupleReplyFile.name : t('vendorDetail.attach')}
                      <input type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" style={{ display: 'none' }}
                        onChange={(e) => setCoupleReplyFile(e.target.files[0] || null)} />
                    </label>
                    {coupleReplyFile && <button onClick={() => setCoupleReplyFile(null)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '12px' }}>✕</button>}
                    <div style={{ flex: 1 }} />
                    <button
                      className="btn btn-primary"
                      disabled={coupleSending || !coupleReply.trim()}
                      onClick={async () => {
                        if (!coupleReply.trim()) return;
                        setCoupleSending(true);
                        try {
                          await sendInquiryMessage(existingInquiry.id, coupleReply.trim(), coupleReplyFile || undefined);
                          setCoupleReply('');
                          setCoupleReplyFile(null);
                          // Refresh conversation
                          const res = await getMyVendorInquiry(vendor.id);
                          if (res.data) setExistingInquiry(res.data);
                        } catch (err) {
                          alert('Failed to send: ' + (err.response?.data?.error || err.message));
                        } finally { setCoupleSending(false); }
                      }}
                      style={{ fontSize: '12px', padding: '6px 14px', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      <Send size={12} /> {coupleSending ? t('common.sending') : t('common.send')}
                    </button>
                  </div>
                </div>
              )}
              <div style={{ marginTop: '6px', textAlign: 'center' }}>
                <span style={{ fontSize: '11px', color: '#6b7280', textTransform: 'capitalize' }}>Status: {existingInquiry.status}</span>
              </div>
            </div>
          )}

          {showInquiry && vendor.is_verified && (
            <div className="sidebar-card inquiry-form-card">
              <div className="inquiry-form-header">
                <Send size={16} />
                <h3>{t('vendorDetail.sendMessageHeading')}</h3>
              </div>
              <div className="inquiry-form-body">
                {inquirySuccess ? (
                  <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <div style={{ fontSize: '40px', marginBottom: '8px' }}>✅</div>
                    <p style={{ color: '#16a34a', fontWeight: 600, marginBottom: '8px' }}>{t('vendorDetail.inquirySent')}</p>
                    <p style={{ fontSize: '13px', color: '#6b7280' }}>{t('vendorDetail.inquirySentDesc')}</p>
                    <button className="btn btn-outline btn-block" style={{ marginTop: '12px' }} onClick={() => { setShowInquiry(false); setInquirySuccess(false); setInquiryForm({ message: '', wedding_date: '', guest_count: '' }); }}>{t('common.close')}</button>
                  </div>
                ) : (
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    setInquirySending(true);
                    setInquiryError('');
                    try {
                      await inquireVendor(vendor.id, {
                        message: inquiryForm.message,
                        wedding_date: inquiryForm.wedding_date || null,
                        guest_count: inquiryForm.guest_count ? parseInt(inquiryForm.guest_count) : null,
                      });
                      setInquirySuccess(true);
                      getMyVendorInquiry(vendor.id).then(res => { if (res.data) setExistingInquiry(res.data); }).catch(() => {});
                    } catch (err) {
                      setInquiryError(err.response?.data?.error || t('vendorDetail.inquiryFailed'));
                    } finally {
                      setInquirySending(false);
                    }
                  }}>
                    {inquiryError && <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '12px' }}>{inquiryError}</div>}
                    <textarea
                      placeholder={t('vendorDetail.tellPlans')}
                      rows={4}
                      required
                      value={inquiryForm.message}
                      onChange={(e) => setInquiryForm(f => ({ ...f, message: e.target.value }))}
                      className="inq-input"
                    />
                    <label className="inq-label">{t('vendorDetail.eventDate')}</label>
                    <input
                      type="date"
                      value={inquiryForm.wedding_date}
                      onChange={(e) => setInquiryForm(f => ({ ...f, wedding_date: e.target.value }))}
                      className="inq-input"
                    />
                    <input
                      type="number"
                      placeholder={t('vendorDetail.guestCount')}
                      value={inquiryForm.guest_count}
                      onChange={(e) => setInquiryForm(f => ({ ...f, guest_count: e.target.value }))}
                      className="inq-input"
                    />
                    <button type="submit" className="btn btn-primary btn-block" disabled={inquirySending}>
                      {inquirySending ? t('common.loading') : t('vendorDetail.sendMessage')}
                    </button>
                  </form>
                )}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

export default VendorDetail;
